import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { OptionalId, ProjectFeeSet, TranslatableText } from '@/types';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { Button, ButtonBar, Dialog } from '@ygtechnologies/rubus-ui';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import FeeEditor from '@/seller/components/fees/FeeEditor';
import { useMergeState } from '@/utils/hooks';
import { ValidatingInput, ValidationForm, ValidationSummary } from '@/components/forms/validation';
import { HttpStatusCode } from '@/HttpStatusCodes';

interface ProjectFeeCreateDialogProps {
    onClose(): void;
    onAccept(data: OptionalId<ProjectFeeSet>): Promise<any> | void;
    show: boolean;
}

function getEmptyItem(): OptionalId<ProjectFeeSet> {
    return { name: '', isDefault: false, fees: [] };
}

const ProjectFeeCreateDialog: React.FC<ProjectFeeCreateDialogProps> = ({ show, onClose, onAccept }) => {
    const [saving, setSaving] = useState(false);
    const [editingFeeSet, mergeEditingFeeSet, setEditingFeeSet] = useMergeState(getEmptyItem());
    const [externalErrors, setExternalErrors] = useState<TranslatableText[]>([]);

    const handleOpen = async () => {
        setSaving(false);
        setEditingFeeSet(getEmptyItem());
    };

    const handleCancelClick = () => {
        triggerHandler(onClose);
    };

    const handleAccept = async (e) => {
        setSaving(true);
        setExternalErrors([]);
        try {
            await triggerHandler(onAccept, editingFeeSet!);
            triggerHandler(onClose);
        } catch (e: any) {
            if (e.status === HttpStatusCode.Conflict) {
                const errorMessage = <Trans>A fee set with this name already exists</Trans>;
                Toast.error(errorMessage);
                setExternalErrors([...externalErrors, errorMessage]);
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={show}
            onOpenChange={(open) => {
                if (!open) handleCancelClick();
            }}
            onOpen={handleOpen}>
            <Dialog.Content contentWidth="lg">
                <ValidationForm onSubmit={handleAccept}>
                    <Dialog.Header>
                        <Trans>New project fee</Trans>
                    </Dialog.Header>
                    <div className="form-group">
                        <ValidatingInput
                            className="form-control"
                            placeholder={t`Name`}
                            value={editingFeeSet.name}
                            onChange={(e) => mergeEditingFeeSet({ name: e.target.value })}
                            required
                        />
                    </div>

                    <div className="form-group">
                        <FeeEditor
                            value={editingFeeSet.fees}
                            onChange={(data) => mergeEditingFeeSet({ fees: data })}
                            previewQuantity={1}
                            previewBasePrice={1000}
                        />
                    </div>
                    <div className="form-group">
                        <ValidationSummary externalErrors={externalErrors} />
                    </div>
                    <ButtonBar>
                        <ButtonBar.Right>
                            <Button variant="tertiary" onClick={handleCancelClick} disabled={saving}>
                                <Trans>Cancel</Trans>
                            </Button>
                            <Button variant="primary" loading={saving} type="submit">
                                <Trans>Save</Trans>
                            </Button>
                        </ButtonBar.Right>
                    </ButtonBar>
                </ValidationForm>
            </Dialog.Content>
        </Dialog>
    );
};

export default ProjectFeeCreateDialog;
